import { LineMask } from '@/components/line-mask';
import { Reveal } from '@/components/reveal';
import { cn } from '@/lib/cn';
import type { TimelineBuilder } from '@/scroll/use-reveal-on-enter';

type Timeline = Parameters<TimelineBuilder>[0];
type Selector = Parameters<TimelineBuilder>[1];

type Size = 'display' | 'column';

interface SectionHeaderProps {
  eyebrow: string;
  /** One entry per visual line, each revealed by its own line mask. */
  title: readonly string[];
  lede?: string;
  /** `display` for a header that owns the row; `column` when it shares the row with media. */
  size?: Size;
  className?: string;
}

const titleClass: Record<Size, string> = {
  display: 'text-[clamp(2rem,4.6vw,3.6rem)] leading-[1.02]',
  column: 'text-[clamp(1.85rem,3.4vw,2.7rem)] leading-[1.04]',
};

/**
 * Eyebrow with the brand-red rule, line-masked title and muted lede, in that order. Every
 * section after the hero opens with one; `playHeader` adds its reveal to the section timeline.
 */
export function SectionHeader({
  eyebrow,
  title,
  lede,
  size = 'display',
  className,
}: SectionHeaderProps) {
  return (
    <div className={cn('grid gap-5', className)}>
      <Reveal as="p" data-eyebrow className="hud eyebrow m-0 text-brand-sky">
        <span aria-hidden className="h-px w-6 bg-brand-red" />
        {eyebrow}
      </Reveal>
      <LineMask
        as="h2"
        data-title
        lines={[...title]}
        className={cn('m-0 font-bold tracking-[-0.04em]', titleClass[size])}
      />
      {lede ? (
        <Reveal
          as="p"
          data-lede
          className="m-0 max-w-[560px] text-[clamp(1rem,1.4vw,1.15rem)] leading-relaxed text-muted-foreground"
        >
          {lede}
        </Reveal>
      ) : null}
    </div>
  );
}

/**
 * Reveals the header from `at` seconds into the section timeline: eyebrow, then the title
 * lines sliding up, then the lede. Returns the time at which the section body should start so
 * it follows the lede rather than racing it.
 */
export function playHeader(tl: Timeline, q: Selector, at = 0): number {
  tl.to(q('[data-eyebrow]'), { opacity: 1, y: 0, duration: 0.6 }, at).to(
    q('[data-title] [data-line]'),
    { yPercent: 0, duration: 1.1, ease: 'power4.out', stagger: 0.1 },
    at + 0.1,
  );
  const lede = q('[data-lede]');
  if (lede.length) tl.to(lede, { opacity: 1, y: 0, duration: 0.7 }, at + 0.45);
  return at + 0.55;
}
